import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageCircle, Mail, Loader2, CheckCircle } from 'lucide-react';
import { submitForm } from '../lib/api';
import { getWhatsAppLink, getEmailLink } from '../lib/utils';

export const FormModal = ({ isOpen, onClose, formType, title }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    project_type: '',
    budget: '',
    product: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await submitForm({
        form_type: formType,
        name: formData.name,
        email: formData.email,
        project_type: formData.project_type || null,
        budget: formData.budget || null,
        product: formData.product || null,
        subject: formData.subject || null,
        message: formData.message,
      });
      setIsSuccess(true);
      setTimeout(() => {
        onClose();
        setIsSuccess(false);
        setFormData({
          name: '',
          email: '',
          project_type: '',
          budget: '',
          product: '',
          subject: '',
          message: '',
        });
      }, 2000);
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectTypes = [
    { value: 'mobile', label: 'Application mobile' },
    { value: 'web', label: 'Site web' },
    { value: 'saas', label: 'SaaS' },
    { value: 'other', label: 'Autre' },
  ];

  const subjects = [
    { value: 'information', label: 'Information' },
    { value: 'partnership', label: 'Partenariat' },
    { value: 'support', label: 'Support' },
    { value: 'other', label: 'Autre' },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
          data-testid="form-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-[#152840] rounded-2xl border border-white/10 overflow-hidden"
            data-testid="form-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h3 className="text-xl font-semibold text-white">{title}</h3>
              <button
                onClick={onClose}
                className="p-2 text-white/60 hover:text-white transition-colors"
                data-testid="form-modal-close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {isSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-8"
                >
                  <CheckCircle size={64} className="mx-auto text-green-500 mb-4" />
                  <h4 className="text-xl font-semibold text-white mb-2">Message envoyé !</h4>
                  <p className="text-white/60">Nous vous répondrons bientôt.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/80 text-sm mb-2">Nom *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg input-dark"
                        placeholder="Votre nom"
                        data-testid="form-input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg input-dark"
                        placeholder="votre@email.com"
                        data-testid="form-input-email"
                      />
                    </div>
                  </div>

                  {(formType === 'project') && (
                    <>
                      <div>
                        <label className="block text-white/80 text-sm mb-2">Type de projet</label>
                        <select
                          name="project_type"
                          value={formData.project_type}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg input-dark"
                          data-testid="form-select-project-type"
                        >
                          <option value="">Sélectionnez...</option>
                          {projectTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-white/80 text-sm mb-2">Budget (optionnel)</label>
                        <input
                          type="text"
                          name="budget"
                          value={formData.budget}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg input-dark"
                          placeholder="ex: 5000€ - 10000€"
                          data-testid="form-input-budget"
                        />
                      </div>
                    </>
                  )}

                  {formType === 'demo' && (
                    <div>
                      <label className="block text-white/80 text-sm mb-2">Produit concerné</label>
                      <input
                        type="text"
                        name="product"
                        value={formData.product}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg input-dark"
                        placeholder="ex: JoSmart Chat"
                        data-testid="form-input-product"
                      />
                    </div>
                  )}

                  {formType === 'product' && (
                    <div>
                      <label className="block text-white/80 text-sm mb-2">Sujet</label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg input-dark"
                        data-testid="form-select-subject"
                      >
                        <option value="">Sélectionnez...</option>
                        {subjects.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-white/80 text-sm mb-2">
                      {formType === 'idea' ? 'Décrivez votre idée *' : 'Message *'}
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 rounded-lg input-dark resize-none"
                      placeholder={formType === 'idea' ? 'Décrivez votre idée en détail...' : 'Votre message...'}
                      data-testid="form-textarea-message"
                    />
                  </div>

                  {error && (
                    <p className="text-red-400 text-sm" data-testid="form-error">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 bg-[#4A90D9] text-white rounded-full font-semibold hover:bg-[#5BA0E9] transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="form-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      'Envoyer'
                    )}
                  </button>
                </form>
              )}

              {/* Contact alternatives */}
              {!isSuccess && (
                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-white/60 text-sm text-center mb-4">Ou contactez-nous directement</p>
                  <div className="flex gap-4 justify-center">
                    <a
                      href={getWhatsAppLink()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white rounded-full text-sm font-medium hover:bg-[#20BD5A] transition-colors"
                      data-testid="form-whatsapp-link"
                    >
                      <MessageCircle size={16} />
                      WhatsApp
                    </a>
                    <a
                      href={getEmailLink()}
                      className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium hover:bg-white/20 transition-colors"
                      data-testid="form-email-link"
                    >
                      <Mail size={16} />
                      Email
                    </a>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FormModal;
